/**
 * PhoneDialer — keypad dropdown triggered from the Header phone icon.
 *
 * Features (#401 polish pass):
 *  - Input formats live as (XXX) XXX-XXXX; underlying digits used for dial
 *  - Paste normalization — strips (, ), -, spaces, leading "+1"/"1"
 *  - Contact search on ANY input >=3 chars (names OR digits). Phone matches
 *    route through the existing searchContacts ILIKE on phone.
 *  - Compliance badges on contact rows — DND / SMS opt-out surfaced before
 *    the rep dials so they know the call may be blocked.
 *  - Recent dials stored as { phone, name, contactId, calledAt } so the
 *    list can show who was called, not just a 10-digit blob. Migrates
 *    legacy string-only entries transparently.
 *  - Recents sourced first from /api/voice/calls (real call history),
 *    fallback to localStorage when the endpoint is empty / unreachable.
 *  - Clear-all + per-row remove controls on the recents list.
 *  - Phone icon affordance on each recent row so it's visibly clickable.
 *  - Physical keyboard digits + star + hash send DTMF during an active
 *    call (previously only on-screen buttons did).
 *  - Web Audio DTMF tone on each keypress — desk-phone-like feel.
 *  - Escape clears the draft number AND closes the dialer.
 *  - Contact search limit 15 (was 5) — bigger tenants don't lose matches.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Phone, Delete, Clock, X, Ban, MessageCircleOff, Trash2 } from 'lucide-react';
import { apiGet, apiPost } from '../../hooks/useApi';
import { useMyNumbers } from '../../hooks/useMyNumbers';
import { FromNumberPicker } from '../shared/FromNumberPicker';
import { digitsOnly, toE164, formatPhone } from '../../../lib/phone-format';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'] as const;

const STORAGE_KEY = 'apex_recent_dials_v2';
const LEGACY_STORAGE_KEY = 'apex_recent_dials';
const MAX_RECENTS = 10;
const SEARCH_DEBOUNCE_MS = 300;
const CONTACT_SEARCH_LIMIT = 15;

// ── Types ────────────────────────────────────────────────────────────────

interface SearchResult {
  id: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  dnd?: boolean;
  sms_opt_out?: boolean;
}

interface RecentEntry {
  phone: string;
  name?: string | null;
  contactId?: string | null;
  calledAt: number; // ms since epoch
}

interface Softphone {
  dial: (
    contactId: string | null,
    contactName: string | null,
    phone: string,
    fromNumber: string,
  ) => string | null;
  sendDtmf: (digits: string) => void;
  getActiveCall: () => { phase: string; from?: string; direction?: string } | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────

function getSoftphone(): Softphone | null {
  return (window as unknown as Record<string, unknown>)['ApexSoftphone'] as Softphone | null;
}

/** True when the input looks like a phone number (digits only, no letters). */
function isDigitMode(input: string): boolean {
  return input.length > 0 && !/[a-zA-Z]/.test(input);
}

function relativeTime(ms: number): string {
  const diff = Date.now() - ms;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ── Recents storage ──────────────────────────────────────────────────────

/**
 * Read recents from localStorage. Handles two kinds of legacy data:
 *   1. `apex_recent_dials` flat string array (pre-v2).
 *   2. `apex_recent_dials_v2` rich entries whose `phone` was stored as
 *      bare digits (10 or 11) by an earlier version of this file. We
 *      up-convert to E.164 on read so the dialer no longer shows
 *      `(165) 623-7900` for a `15027446660` entry.
 *
 * Anything we can't coerce to E.164 is dropped — better to lose a stale
 * recent than to show a mis-formatted one that won't dial.
 */
function readRecents(): RecentEntry[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed: unknown = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        const fixed: RecentEntry[] = [];
        let mutated = false;
        for (const entry of parsed as RecentEntry[]) {
          if (!entry?.phone) continue;
          const e164 = entry.phone.startsWith('+') ? entry.phone : toE164(entry.phone);
          if (!e164) {
            mutated = true;
            continue;
          }
          if (e164 !== entry.phone) mutated = true;
          fixed.push({ ...entry, phone: e164 });
        }
        if (mutated) {
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(fixed));
          } catch {
            // ignore write-back failure
          }
        }
        return fixed;
      }
    }
    // Legacy fallback: flat string array
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacy) {
      const parsed: unknown = JSON.parse(legacy);
      if (Array.isArray(parsed)) {
        const migrated: RecentEntry[] = [];
        (parsed as string[]).forEach((phone, i) => {
          const e164 = toE164(phone);
          if (!e164) return;
          migrated.push({
            phone: e164,
            // Space them out so order is preserved; oldest gets the earliest time.
            calledAt: Date.now() - (i + 1) * 60_000,
          });
        });
        localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
        return migrated;
      }
    }
  } catch {
    // localStorage disabled or corrupt — return empty and forget about it
  }
  return [];
}

function writeRecents(entries: RecentEntry[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(0, MAX_RECENTS)));
  } catch {
    // ignore — localStorage may be disabled
  }
}

function addRecent(entry: RecentEntry): RecentEntry[] {
  const existing = readRecents().filter((e) => e.phone !== entry.phone);
  existing.unshift(entry);
  const trimmed = existing.slice(0, MAX_RECENTS);
  writeRecents(trimmed);
  return trimmed;
}

// ── DTMF tones (Web Audio) ───────────────────────────────────────────────

const DTMF_FREQS: Record<string, [number, number]> = {
  '1': [697, 1209],
  '2': [697, 1336],
  '3': [697, 1477],
  '4': [770, 1209],
  '5': [770, 1336],
  '6': [770, 1477],
  '7': [852, 1209],
  '8': [852, 1336],
  '9': [852, 1477],
  '*': [941, 1209],
  '0': [941, 1336],
  '#': [941, 1477],
};

let audioCtx: AudioContext | null = null;
function playTone(key: string) {
  const pair = DTMF_FREQS[key];
  if (!pair) return;
  try {
    if (!audioCtx) {
      const Ctx =
        (
          window as unknown as {
            AudioContext?: typeof AudioContext;
            webkitAudioContext?: typeof AudioContext;
          }
        ).AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return;
      audioCtx = new Ctx();
    }
    const ctx = audioCtx;
    const now = ctx.currentTime;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.15, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
    gain.connect(ctx.destination);
    for (const freq of pair) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      osc.connect(gain);
      osc.start(now);
      osc.stop(now + 0.13);
    }
  } catch {
    // Audio may be blocked (user gesture requirement); silently ignore
  }
}

// ── Voice-history recents ────────────────────────────────────────────────

interface VoiceCallHistoryRow {
  id: string;
  direction: string;
  from_number?: string | null;
  to_number?: string | null;
  contact_id?: string | null;
  contact_first_name?: string | null;
  contact_last_name?: string | null;
  started_at?: string | null;
  created_at?: string | null;
}

/**
 * Fetch the rep's recent outbound calls from voice history. De-dupes by
 * phone number, preserves the most recent call's name/contact association.
 */
async function fetchHistoryRecents(): Promise<RecentEntry[]> {
  try {
    const res = await apiGet<{ data: VoiceCallHistoryRow[] }>(
      `/api/voice/calls?direction=outbound&per_page=25&page=1`,
    );
    const rows = res.data ?? [];
    const seen = new Set<string>();
    const out: RecentEntry[] = [];
    for (const row of rows) {
      // Store in E.164 so the recent is directly dialable via softphone
      // without additional normalization at dial time.
      const e164 = toE164(row.to_number ?? '');
      if (!e164 || seen.has(e164)) continue;
      seen.add(e164);
      const name =
        [row.contact_first_name, row.contact_last_name].filter(Boolean).join(' ') || null;
      const calledAt = row.started_at
        ? new Date(row.started_at).getTime()
        : row.created_at
          ? new Date(row.created_at).getTime()
          : Date.now();
      out.push({
        phone: e164,
        name,
        contactId: row.contact_id ?? null,
        calledAt: Number.isFinite(calledAt) ? calledAt : Date.now(),
      });
      if (out.length >= MAX_RECENTS) break;
    }
    return out;
  } catch {
    return [];
  }
}

// ── Component ────────────────────────────────────────────────────────────

export function PhoneDialer() {
  const {
    numbers: myNumbers,
    fromNumber,
    canVoice,
    setFromNumber,
    loading: numbersLoading,
  } = useMyNumbers();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [recents, setRecents] = useState<RecentEntry[]>([]);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Derived values
  const activeCall = getSoftphone()?.getActiveCall() ?? null;
  const isInCall = !!activeCall;
  const inputDigits = useMemo(() => digitsOnly(input), [input]);
  const displayValue = useMemo(() => {
    if (!input) return '';
    // Show letters through for name search; format only pure-digit input
    if (!isDigitMode(input)) return input;
    return formatPhone(inputDigits);
  }, [input, inputDigits]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // On open: focus input and load recents (history first, fall back to local)
  useEffect(() => {
    if (!open) return;
    setTimeout(() => inputRef.current?.focus(), 50);
    // Show localStorage recents immediately, then replace with history if present.
    const local = readRecents();
    setRecents(local);
    void fetchHistoryRecents().then((remote) => {
      if (remote.length > 0) setRecents(remote);
    });
  }, [open]);

  // Contact search — fires on 3+ chars whether it's a name OR digits.
  useEffect(() => {
    const q = input.trim();
    if (q.length < 3) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        // For digit queries, search by the raw digits; for mixed/letter
        // queries, send as-is — the smart-query brain handles tokenization
        // and the phone-vs-text routing per token.
        const searchQ = isDigitMode(q) ? inputDigits : q;
        const data = await apiPost<{ data: SearchResult[] }>('/api/contacts/search', {
          q: searchQ,
          columns: ['first_name', 'last_name', 'phone', 'dnd', 'sms_opt_out'],
          limit: CONTACT_SEARCH_LIMIT,
        });
        setSearchResults(data.data || []);
      } catch {
        setSearchResults([]);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [input, inputDigits]);

  // Physical keyboard → DTMF during active call (new). The input retains
  // focus, so we hook keydown at the document level only when a call is
  // active and the dialer is open.
  useEffect(() => {
    if (!open || !isInCall) return;
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      // Ignore when the user is typing into any input/textarea — they're
      // composing text, not dialing tones.
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      const key = e.key;
      if (DTMF_FREQS[key]) {
        playTone(key);
        getSoftphone()?.sendDtmf(key);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, isInCall]);

  // ── Handlers ──────────────────────────────────────────────────────────

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Letters present → name mode; pass through.
    if (/[a-zA-Z]/.test(raw)) {
      setInput(raw);
      return;
    }
    // Digits / paste. Strip (, ), -, spaces, + (we'll re-add on dial).
    // Also strip a leading "1" when the total is 11 digits — US country
    // code that we don't want tainting the display formatter.
    const d = digitsOnly(raw);
    const cleaned = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
    // Cap at 15 digits (ITU-T E.164 max); prevents runaway input.
    setInput(cleaned.slice(0, 15));
  };

  const handleKeyPress = useCallback(
    (key: string) => {
      playTone(key);
      if (isInCall) {
        getSoftphone()?.sendDtmf(key);
        return;
      }
      // Only append to digits portion if we're in digit mode. If the user
      // has started typing a name, keypad presses append literally.
      setInput((prev) => {
        if (!/[a-zA-Z]/.test(prev)) return digitsOnly(prev + key);
        return prev + key;
      });
    },
    [isInCall],
  );

  const handleBackspace = () => {
    setInput((prev) => {
      if (/[a-zA-Z]/.test(prev)) return prev.slice(0, -1);
      return digitsOnly(prev.slice(0, -1));
    });
  };

  const handleDial = (dialNumber?: string, contactId?: string, contactName?: string) => {
    // Everything gets normalized to E.164 before hitting the softphone.
    // The softphone (and apex-voice downstream) expects `+1...` — passing
    // bare 10-digit or 11-digit `1...` results in the request succeeding
    // client-side but apex-voice rejecting / no call being placed.
    const phone = toE164(dialNumber ?? inputDigits);
    if (!phone) {
      window.dispatchEvent(
        new CustomEvent('apex:dial-error', { detail: { reason: 'No phone number to dial.' } }),
      );
      return;
    }
    const sp = getSoftphone();
    if (!sp) {
      window.dispatchEvent(
        new CustomEvent('apex:dial-error', {
          detail: { reason: 'Softphone is still loading. Try again in a moment.' },
        }),
      );
      return;
    }
    sp.dial(contactId ?? null, contactName ?? null, phone, fromNumber);
    const updated = addRecent({
      phone,
      name: contactName ?? null,
      contactId: contactId ?? null,
      calledAt: Date.now(),
    });
    setRecents(updated);
    setInput('');
    setOpen(false);
  };

  const handleContactDial = (result: SearchResult) => {
    if (!result.phone) return;
    const name = [result.first_name, result.last_name].filter(Boolean).join(' ');
    handleDial(result.phone, result.id, name);
  };

  const handleRemoveRecent = (phone: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const next = recents.filter((r) => r.phone !== phone);
    setRecents(next);
    writeRecents(next);
  };

  const handleClearRecents = () => {
    setRecents([]);
    writeRecents([]);
    try {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      // ignore
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (inputDigits || input.trim())) {
      e.preventDefault();
      if (isDigitMode(input) && inputDigits) {
        handleDial();
      }
    }
    if (e.key === 'Escape') {
      setInput('');
      setOpen(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="w-8 h-8 rounded-full bg-brand-green/10 text-brand-green hover:bg-brand-green/20 flex items-center justify-center transition-colors cursor-pointer border border-brand-green/20"
        title="Phone dialer"
      >
        <Phone className="w-4 h-4 stroke-[2]" />
      </button>

      {open && (
        // Layout: fixed max height, input + keypad pinned top-to-middle,
        // search-results / recents share a flexible scrollable region below
        // the keypad. Previously the whole column grew with search results,
        // pushing the keypad up and down as results flickered in and out.
        <div className="absolute top-full right-0 mt-2 w-[300px] max-h-[min(720px,calc(100vh-80px))] bg-card border border-border/80 rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] z-50 overflow-hidden flex flex-col">
          {/* Input */}
          <div className="p-3 border-b border-border/60 flex-shrink-0">
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={displayValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={isInCall ? 'Send DTMF...' : 'Enter number or name...'}
                inputMode={isInCall || isDigitMode(input) ? 'tel' : 'text'}
                autoComplete="off"
                className="w-full px-3 py-2.5 text-[0.9375rem] font-mono text-center bg-secondary/50 border border-border/60 rounded-xl focus:border-primary/40 focus:ring-2 focus:ring-primary/10 outline-none transition-all"
              />
              {input && (
                <button
                  onClick={() => setInput('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-md flex items-center justify-center hover:bg-secondary transition-colors cursor-pointer"
                  aria-label="Clear"
                >
                  <X className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              )}
            </div>
          </div>

          {/* Active-call indicator (#484). When a call is in progress the
              picker itself is moot — the outbound line is already locked in
              — so show the active from-number in place of the picker until
              the call ends. Outbound only; inbound shows caller in the
              ActiveCallWidget. */}
          {isInCall && activeCall?.direction === 'outbound' && activeCall.from && (
            <div className="px-3 pt-3 flex items-center gap-2 text-[0.6875rem] text-muted-foreground flex-shrink-0">
              <span className="font-medium">On call from</span>
              <span className="font-mono text-foreground">{activeCall.from}</span>
            </div>
          )}

          {/* From-number picker (#475, #634). Shared across every outbound
              surface via useMyNumbers. Shows all accessible numbers with
              capability icons; the Dial button gates on canVoice for the
              current selection. Hidden while a call is in progress — the
              active-call strip above takes its place. */}
          {!isInCall && (
            <FromNumberPicker
              numbers={myNumbers}
              value={fromNumber}
              onChange={setFromNumber}
              loading={numbersLoading}
              label="From"
              ariaLabel="Outbound phone number"
              className="px-3 pt-3 flex-shrink-0"
            />
          )}

          {/* Keypad — pinned right under the input. Search results and
              recents share a flexible scrollable region BELOW the keypad
              (see below) so the keypad never jumps around as results
              appear/disappear. */}
          <div className="p-3 flex-shrink-0">
            <div className="grid grid-cols-3 gap-1.5">
              {KEYS.map((key) => (
                <button
                  key={key}
                  onClick={() => handleKeyPress(key)}
                  className="h-11 rounded-xl bg-secondary/50 hover:bg-secondary text-[1rem] font-semibold text-foreground flex items-center justify-center transition-colors cursor-pointer active:scale-95"
                >
                  {key}
                </button>
              ))}
            </div>

            {/* Action row */}
            <div className="flex items-center gap-2 mt-2">
              <button
                onClick={handleBackspace}
                disabled={!input}
                className="flex-1 h-10 rounded-xl bg-secondary/50 hover:bg-secondary text-muted-foreground flex items-center justify-center transition-colors cursor-pointer disabled:opacity-30"
                aria-label="Backspace"
              >
                <Delete className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDial()}
                disabled={!inputDigits || isInCall || !fromNumber || !canVoice}
                title={
                  !fromNumber && !numbersLoading
                    ? 'No accessible outbound number — ask your admin to assign one'
                    : !canVoice && !numbersLoading
                      ? 'Selected number does not support voice — pick a call-capable number'
                      : undefined
                }
                className="flex-[2] h-10 rounded-xl bg-brand-green text-white font-semibold text-[0.8125rem] hover:bg-brand-green/90 flex items-center justify-center gap-2 transition-colors cursor-pointer disabled:opacity-50 active:scale-[0.98]"
              >
                <Phone className="w-4 h-4" />
                {isInCall ? 'In Call' : 'Dial'}
              </button>
            </div>
          </div>

          {/* Search results — render in place of recents when active so the
              keypad position never shifts. */}
          {searchResults.length > 0 && (
            <div className="border-t border-border/60 max-h-[240px] overflow-y-auto">
              <div className="px-3 py-1.5 text-[0.625rem] font-bold text-muted-foreground uppercase tracking-wider">
                Contacts
              </div>
              {searchResults.map((r) => {
                const name = [r.first_name, r.last_name].filter(Boolean).join(' ') || '(no name)';
                const blocked = r.dnd || r.sms_opt_out;
                return (
                  <button
                    key={r.id}
                    onClick={() => handleContactDial(r)}
                    disabled={!r.phone}
                    className="w-full px-3 py-2 flex items-center gap-2 hover:bg-secondary/50 transition-colors text-left cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-[0.75rem] font-medium text-foreground truncate flex items-center gap-1.5">
                        <span className="truncate">{name}</span>
                        {r.dnd && (
                          <span title="Do Not Disturb" className="text-destructive">
                            <Ban className="w-3 h-3" />
                          </span>
                        )}
                        {r.sms_opt_out && (
                          <span title="SMS opt-out" className="text-amber-500">
                            <MessageCircleOff className="w-3 h-3" />
                          </span>
                        )}
                      </div>
                      {r.phone && (
                        <div className="text-[0.6875rem] text-muted-foreground font-mono truncate">
                          {formatPhone(r.phone) || r.phone}
                        </div>
                      )}
                    </div>
                    {r.phone && !blocked && (
                      <Phone className="w-3.5 h-3.5 text-brand-green stroke-[2] flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Recents */}
          {recents.length > 0 && searchResults.length === 0 && (
            <div className="border-t border-border/60 max-h-[180px] overflow-y-auto">
              <div className="px-3 py-1.5 text-[0.625rem] font-bold text-muted-foreground uppercase tracking-wider flex items-center justify-between">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Recent
                </span>
                <button
                  onClick={handleClearRecents}
                  className="text-[0.625rem] normal-case font-medium text-muted-foreground hover:text-destructive transition-colors cursor-pointer"
                  title="Clear all recent dials"
                >
                  Clear
                </button>
              </div>
              {recents.map((r) => (
                <div
                  key={r.phone}
                  className="w-full px-3 py-2 flex items-center gap-2 hover:bg-secondary/50 transition-colors group"
                >
                  <button
                    onClick={() =>
                      handleDial(r.phone, r.contactId ?? undefined, r.name ?? undefined)
                    }
                    className="flex-1 min-w-0 text-left cursor-pointer"
                  >
                    {r.name ? (
                      <div className="text-[0.75rem] font-medium text-foreground truncate">
                        {r.name}
                      </div>
                    ) : null}
                    <div className="flex items-center gap-2">
                      <span className="text-[0.6875rem] text-muted-foreground font-mono">
                        {formatPhone(r.phone) || r.phone}
                      </span>
                      <span className="text-[0.625rem] text-muted-foreground/70">
                        · {relativeTime(r.calledAt)}
                      </span>
                    </div>
                  </button>
                  <button
                    onClick={() =>
                      handleDial(r.phone, r.contactId ?? undefined, r.name ?? undefined)
                    }
                    className="p-1 rounded-md text-brand-green hover:bg-brand-green/10 cursor-pointer"
                    aria-label="Dial"
                    title="Dial"
                  >
                    <Phone className="w-3.5 h-3.5 stroke-[2]" />
                  </button>
                  <button
                    onClick={(e) => handleRemoveRecent(r.phone, e)}
                    className="p-1 rounded-md text-muted-foreground/60 hover:text-destructive hover:bg-destructive/10 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Remove from recents"
                    title="Remove from recents"
                  >
                    <Trash2 className="w-3 h-3 stroke-[2]" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
